// WhatsApp-style link rendering for agent messages: bare/Markdown URLs become
// safe anchors, and the first link gets an inline mini video player or a page
// summary card. All text goes through textContent — never innerHTML.

export interface LinkPreviewData {
  url: string;
  kind: 'page' | 'video' | 'image';
  title?: string;
  description?: string;
  siteName?: string;
  image?: string;
  videoUrl?: string;
}

export type LinkPreviewResolver = (url: string) => Promise<LinkPreviewData | null>;

export interface FoundLink {
  start: number;
  end: number;
  href: string;
  label: string;
}

const LINK_RE = /\[([^\]\n]{1,300})\]\((https?:\/\/[^\s()<>]+(?:\([^\s()<>]*\)[^\s()<>]*)*)\)|((?:https?:\/\/|\bwww\.)[^\s<>"'`]+)/gi;

function count(s: string, ch: string): number { return s.split(ch).length - 1; }

/** Drop sentence punctuation / Markdown emphasis glued to the end of a bare URL. */
function trimUrl(raw: string): string {
  let url = raw;
  for (;;) {
    const last = url.slice(-1);
    if ('.,;:!?*_~'.includes(last) && last) url = url.slice(0, -1);
    else if (last === ')' && count(url, ')') > count(url, '(')) url = url.slice(0, -1);
    else if (last === ']' && count(url, ']') > count(url, '[')) url = url.slice(0, -1);
    else return url;
  }
}

function normalizeHref(raw: string): string | null {
  try {
    const url = new URL(/^www\./i.test(raw) ? `https://${raw}` : raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

export function findLinks(text: string): FoundLink[] {
  const links: FoundLink[] = [];
  for (const m of text.matchAll(LINK_RE)) {
    const start = m.index ?? 0;
    if (m[1] && m[2]) {
      const href = normalizeHref(m[2]);
      if (href) links.push({ start, end: start + m[0].length, href, label: m[1] });
      continue;
    }
    const raw = trimUrl(m[3] || '');
    const href = raw.length > 4 ? normalizeHref(raw) : null;
    if (href) links.push({ start, end: start + raw.length, href, label: raw });
  }
  return links;
}

export function youtubeVideoId(url: URL): string | null {
  const host = url.hostname.toLowerCase().replace(/^(www|m|music)\./, '');
  let id = '';
  if (host === 'youtu.be') id = url.pathname.slice(1).split('/')[0];
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (url.pathname === '/watch') id = url.searchParams.get('v') || '';
    else id = /^\/(?:embed|shorts|live|v)\/([^/]+)/.exec(url.pathname)?.[1] || '';
  }
  return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
}

function vimeoEmbedSrc(url: URL): string | null {
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;
  const m = /\/(\d{5,12})(?:\/([0-9a-f]{6,}))?(?:\/|$)/i.exec(url.pathname);
  if (!m) return null;
  const hash = m[2] || url.searchParams.get('h');
  return `https://player.vimeo.com/video/${m[1]}?autoplay=1${hash && /^[0-9a-f]+$/i.test(hash) ? `&h=${hash}` : ''}`;
}

function directMediaKind(url: URL): 'video' | 'image' | null {
  const path = url.pathname.toLowerCase();
  if (/\.(mp4|webm|ogv|m4v|mov)$/.test(path)) return 'video';
  if (/\.(png|jpe?g|gif|webp|avif)$/.test(path)) return 'image';
  return null;
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, attrs: Record<string, string> = {}): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  return n;
}

function externalAnchor(href: string, cls: string): HTMLAnchorElement {
  return node('a', cls, { href, target: '_blank', rel: 'noopener noreferrer nofollow' });
}

function hostLabel(href: string): string {
  try { return new URL(href).hostname.replace(/^www\./, ''); } catch { return ''; }
}

const PLAY_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';

const resolved = new Map<string, Promise<LinkPreviewData | null>>();

function resolveOnce(href: string, resolver: LinkPreviewResolver): Promise<LinkPreviewData | null> {
  let pending = resolved.get(href);
  if (!pending) {
    pending = resolver(href).catch(() => null);
    resolved.set(href, pending);
  }
  return pending;
}

export interface LinkPreviewOptions {
  resolver?: LinkPreviewResolver | null;
  playLabel: string;
  /** Called whenever the card's height may have changed (async metadata, image load). */
  onResize: () => void;
}

interface Card {
  root: HTMLDivElement;
  media: HTMLDivElement;
  meta: HTMLAnchorElement;
  site: HTMLDivElement;
  title: HTMLDivElement;
  desc: HTMLDivElement;
}

function makeCard(href: string, kind: string): Card {
  const root = node('div', `link-preview lp-${kind}`);
  const media = node('div', 'lp-media');
  const meta = externalAnchor(href, 'lp-meta');
  const site = node('div', 'lp-site');
  const title = node('div', 'lp-title');
  const desc = node('div', 'lp-desc');
  site.textContent = hostLabel(href);
  meta.append(site, title, desc);
  root.append(media, meta);
  root.hidden = true;
  return { root, media, meta, site, title, desc };
}

function fillMeta(card: Card, data: Partial<LinkPreviewData>, opts: LinkPreviewOptions): void {
  if (data.siteName) card.site.textContent = data.siteName;
  card.title.textContent = data.title || '';
  card.desc.textContent = data.description || '';
  card.title.hidden = !data.title;
  card.desc.hidden = !data.description;
  opts.onResize();
}

function setImage(container: HTMLElement, src: string, opts: LinkPreviewOptions, onFail?: () => void): HTMLImageElement {
  const img = node('img', 'lp-thumb', { src, alt: '', loading: 'lazy', referrerpolicy: 'no-referrer' });
  img.addEventListener('load', () => opts.onResize());
  img.addEventListener('error', () => { img.remove(); onFail?.(); opts.onResize(); });
  container.appendChild(img);
  return img;
}

/** Thumbnail + ▶ overlay that swaps to the provider's embedded player on click. */
function mountEmbedPoster(card: Card, embedSrc: string, thumb: string | undefined, opts: LinkPreviewOptions): (thumb: string) => void {
  card.media.classList.add('lp-media-video');
  const poster = node('button', 'lp-poster', { type: 'button', 'aria-label': opts.playLabel, title: opts.playLabel });
  const play = node('span', 'lp-play');
  play.innerHTML = PLAY_SVG;
  poster.appendChild(play);
  card.media.appendChild(poster);
  let thumbEl: HTMLImageElement | null = null;
  const applyThumb = (src: string) => {
    // Poster is detached once the player replaces it.
    if (thumbEl || !poster.parentElement) return;
    thumbEl = setImage(poster, src, opts);
    poster.insertBefore(thumbEl, play);
  };
  if (thumb) applyThumb(thumb);
  poster.addEventListener('click', () => {
    const frame = node('iframe', 'lp-frame', {
      src: embedSrc,
      title: opts.playLabel,
      allow: 'autoplay; encrypted-media; picture-in-picture; fullscreen',
      referrerpolicy: 'strict-origin-when-cross-origin',
      sandbox: 'allow-scripts allow-same-origin allow-presentation allow-popups',
    });
    card.media.replaceChildren(frame);
  });
  return applyThumb;
}

function mountVideo(card: Card, src: string, poster: string | undefined, opts: LinkPreviewOptions): void {
  card.media.classList.add('lp-media-video');
  const video = node('video', 'lp-player', { src, controls: '', preload: 'metadata', playsinline: '' });
  if (poster) video.setAttribute('poster', poster);
  video.addEventListener('loadedmetadata', () => opts.onResize());
  video.addEventListener('error', () => { card.root.remove(); opts.onResize(); });
  card.media.appendChild(video);
}

/**
 * Build the preview for the first link of a message, or null when the link
 * has no local media shape and no resolver is available. The card starts
 * hidden for generic pages and appears only once metadata arrives.
 */
export function buildLinkPreview(href: string, opts: LinkPreviewOptions): HTMLElement | null {
  let url: URL;
  try { url = new URL(href); } catch { return null; }

  const ytId = youtubeVideoId(url);
  const vimeoSrc = ytId ? null : vimeoEmbedSrc(url);
  if (ytId || vimeoSrc) {
    const card = makeCard(href, 'video');
    const embed = ytId
      ? `https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0&playsinline=1`
      : vimeoSrc!;
    const applyThumb = mountEmbedPoster(card, embed, ytId ? `https://i.ytimg.com/vi/${ytId}/mqdefault.jpg` : undefined, opts);
    fillMeta(card, { siteName: ytId ? 'YouTube' : 'Vimeo' }, opts);
    card.root.hidden = false;
    if (opts.resolver) {
      void resolveOnce(href, opts.resolver).then(data => {
        if (!data) return;
        if (data.image) applyThumb(data.image);
        fillMeta(card, data, opts);
      });
    }
    return card.root;
  }

  const direct = directMediaKind(url);
  if (direct === 'video') {
    const card = makeCard(href, 'video');
    mountVideo(card, href, undefined, opts);
    const file = url.pathname.split('/').pop() || '';
    let title = file;
    try { title = decodeURIComponent(file); } catch { /* keep raw */ }
    fillMeta(card, { title }, opts);
    card.root.hidden = false;
    return card.root;
  }
  if (direct === 'image') {
    const wrap = node('div', 'link-preview lp-image');
    const a = externalAnchor(href, 'lp-image-link');
    setImage(a, href, opts, () => wrap.remove());
    wrap.appendChild(a);
    return wrap;
  }

  if (!opts.resolver) return null;
  const card = makeCard(href, 'page');
  void resolveOnce(href, opts.resolver).then(data => {
    if (!data || !card.root.isConnected) { card.root.remove(); return; }
    if (data.kind === 'video' && data.videoUrl) {
      card.root.className = 'link-preview lp-video';
      mountVideo(card, data.videoUrl, data.image, opts);
    } else if (data.image) {
      const link = externalAnchor(href, 'lp-image-link');
      setImage(link, data.image, opts, () => card.media.remove());
      card.media.appendChild(link);
      if (data.kind === 'image') card.meta.remove();
    } else {
      card.media.remove();
    }
    fillMeta(card, data, opts);
    card.root.hidden = false;
    opts.onResize();
  });
  return card.root;
}
